/**
 * Spinner Component
 *
 * Componente de loading spinner com variantes de tamanho e cor.
 * Usado para indicar carregamento de dados ou processamento.
 *
 * @module components/ui
 * @since 2025-12-16
 * @epic INIT-0045-03, INIT-0046
 *
 * @example
 * // Spinner básico (tamanho médio, cor primária)
 * <Spinner />
 *
 * @example
 * // Spinner grande para página inteira
 * <div className="flex justify-center items-center h-screen">
 *   <Spinner size="xl" />
 * </div>
 *
 * @example
 * // Spinner branco para botões com fundo escuro
 * <Button loading>
 *   <Spinner size="sm" color="white" /> Carregando...
 * </Button>
 *
 * @example
 * // Spinner em card de loading
 * {isLoading && (
 *   <div className="flex items-center gap-2">
 *     <Spinner size="sm" color="gray" />
 *     <span>Carregando dados...</span>
 *   </div>
 * )}
 */

import { cn } from '@/lib/utils';

/** Tamanhos disponíveis para o spinner */
type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Props do componente Spinner
 */
interface SpinnerProps {
  /** Tamanho do spinner: sm (16px), md (24px), lg (32px), xl (48px) @default 'md' */
  size?: SpinnerSize;
  /** Classes CSS adicionais */
  className?: string;
  /** Cor do spinner: primary (azul), white, gray @default 'primary' */
  color?: 'primary' | 'white' | 'gray';
}

const sizeClasses: Record<SpinnerSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
  xl: 'h-12 w-12',
};

const colorClasses: Record<string, string> = {
  primary: 'text-primary',
  white: 'text-white',
  gray: 'text-muted-foreground',
};

export function Spinner({ size = 'md', color = 'primary', className }: SpinnerProps) {
  return (
    <svg
      className={cn('animate-spin', sizeClasses[size], colorClasses[color], className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export default Spinner;
